import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, CalendarCheck, Calendar as CalendarIcon,
  CheckSquare, CreditCard, LogOut, Menu, Sparkles, FileText, Receipt,
  Building2, Wallet, Wrench, Search, X, UsersRound, Grid3X3, Video, Camera,
  Inbox, BarChart3, Trash2, Palette, Film, Home, Upload, ChevronDown, ChevronRight,
  Handshake, Briefcase, Package, Settings, ShieldCheck,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABEL, hasFinancialAccess, isManagement, isDivisionUser, isContentCreator, isMuaAdmin, PBH_DIVISION_ID } from "../lib/constants";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { clientName, formatDate } from "../lib/format";
import NotificationBell from "./NotificationBell";
import { RolePreviewButton, RolePreviewBanner } from "./RolePreview";

// buildNavGroups: susun menu berkelompok berdasarkan fungsi pekerjaan.
// Divisi menjadi filter di dalam halaman (bukan menu duplikat per divisi).
// Menu dan tombol mengikuti permission; backend tetap memvalidasi tindakan.
const buildNavGroups = (user) => {
  const owner = user?.role === "owner";
  const gm = user?.role === "general_manager";
  const admin = user?.role === "admin";
  const isCentral = owner || gm || admin || isMuaAdmin(user);
  const canFinance = hasFinancialAccess(user);
  const dm = user?.role === "division_manager";
  const da = user?.role === "division_admin";
  const pbhScope = user?.division_id === PBH_DIVISION_ID;

  // Konten Creator (khusus)
  if (isContentCreator(user)) {
    return [
      { key: "kerja", label: "Pekerjaan Saya", icon: Home, items: [
        { path: "/content", label: "Konten Kalender", icon: Video, testId: "sidebar-nav-content" },
        { path: "/tasks", label: "Tugas Saya", icon: CheckSquare, testId: "sidebar-nav-tasks" },
        { path: "/bookings", label: "Acara", icon: CalendarCheck, testId: "sidebar-nav-bookings" },
        { path: "/calendar", label: "Kalender", icon: CalendarIcon, testId: "sidebar-nav-calendar" },
      ]},
    ];
  }

  // Non-MUA division user — Manager Divisi mendapat menu lengkap (scoped by division),
  // Admin Divisi & Staff hanya dapat workspace + tugas + acara divisi.
  if (isDivisionUser(user) && !isMuaAdmin(user)) {
    const dmFull = user?.role === "division_manager";
    const groups = [
      { key: "beranda", label: "Beranda", icon: Home, items: [
        { path: dmFull ? "/dashboard" : "/workspace", label: dmFull ? "Dasbor Divisi" : "Ruang Kerja", icon: dmFull ? LayoutDashboard : Wrench, testId: dmFull ? "sidebar-nav-dashboard" : "sidebar-nav-workspace" },
        { path: "/tasks", label: "Tugas Saya & Tim", icon: CheckSquare, testId: "sidebar-nav-tasks" },
      ]},
    ];
    if (dmFull) {
      groups.push({ key: "client", label: "Client & Penjualan", icon: Handshake, items: [
        { path: "/clients", label: "Data Client", icon: Users, testId: "sidebar-nav-clients" },
        { path: "/inquiry", label: "Calon Klien", icon: Inbox, testId: "sidebar-nav-inquiry" },
        { path: "/quotations", label: "Penawaran", icon: FileText, testId: "sidebar-nav-quotations" },
      ]});
    } else if (da) {
      groups.push({ key: "client", label: "Client & Penjualan", icon: Handshake, items: [
        { path: "/inquiry", label: "Calon Klien", icon: Inbox, testId: "sidebar-nav-inquiry" },
      ]});
    }
    groups.push({ key: "booking", label: "Booking & Pelaksanaan", icon: CalendarCheck, items: [
      { path: "/bookings", label: dmFull ? "Daftar Booking" : "Acara Divisi", icon: CalendarCheck, testId: "sidebar-nav-bookings" },
      { path: "/bookings/tasks", label: "Tugas per Booking", icon: CheckSquare, testId: "sidebar-nav-bookings-tasks" },
      { path: "/tasks/board", label: "Kanban Board", icon: Grid3X3, testId: "sidebar-nav-tasks-board" },
      { path: "/calendar", label: "Kalender & Jadwal", icon: CalendarIcon, testId: "sidebar-nav-calendar" },
      ...(pbhScope ? [{ path: "/production", label: "Produksi PBH", icon: Film, testId: "sidebar-nav-production" }] : []),
    ]});
    if (dmFull) {
      groups.push({ key: "keuangan", label: "Keuangan", icon: Wallet, items: [
        { path: "/invoices", label: "Invoice Client", icon: Receipt, testId: "sidebar-nav-invoices" },
        { path: "/finance", label: "Ringkasan Keuangan", icon: Wallet, testId: "sidebar-nav-finance" },
        { path: "/vendors", label: "Tagihan Vendor", icon: Building2, testId: "sidebar-nav-vendors" },
        { path: "/wages", label: "Honor Crew", icon: CreditCard, testId: "sidebar-nav-wages" },
      ]});
      groups.push({ key: "tim", label: "Tim & Karyawan", icon: UsersRound, items: [
        { path: "/employees", label: "Data Karyawan Divisi", icon: UsersRound, testId: "sidebar-nav-employees" },
      ]});
      groups.push({ key: "produk", label: "Produk & Website", icon: Package, items: [
        { path: "/pricelist", label: "Harga & Paket Operasional", icon: FileText, testId: "sidebar-nav-pricelist" },
        { path: "/vendors", label: "Kontak Vendor", icon: Building2, testId: "sidebar-nav-vendors-list" },
      ]});
      groups.push({ key: "laporan", label: "Marketing & Laporan", icon: BarChart3, items: [
        { path: "/reports", label: "Laporan Divisi", icon: BarChart3, testId: "sidebar-nav-reports" },
      ]});
    } else if (da) {
      groups.push({ key: "produk", label: "Produk & Website", icon: Package, items: [
        { path: "/pricelist", label: "Harga & Paket Operasional", icon: FileText, testId: "sidebar-nav-pricelist" },
      ]});
      groups.push({ key: "laporan", label: "Marketing & Laporan", icon: BarChart3, items: [
        { path: "/reports", label: "Laporan Divisi", icon: BarChart3, testId: "sidebar-nav-reports" },
      ]});
    }
    return groups;
  }

  // Central admin / Management / Admin MUA
  const groups = [
    { key: "beranda", label: "Beranda", icon: Home, items: [
      { path: "/dashboard", label: "Dasbor", icon: LayoutDashboard, testId: "sidebar-nav-dashboard" },
      { path: "/divisions", label: "Semua Divisi", icon: Grid3X3, testId: "sidebar-nav-divisions" },
    ]},
    { key: "client", label: "Client & Penjualan", icon: Handshake, items: [
      { path: "/clients", label: "Data Client", icon: Users, testId: "sidebar-nav-clients" },
      { path: "/inquiry", label: "Calon Klien", icon: Inbox, testId: "sidebar-nav-inquiry" },
      { path: "/inquiries/pool", label: "Inquiry Pool", icon: Inbox, testId: "sidebar-nav-inquiry-pool" },
      { path: "/quotations", label: "Penawaran", icon: FileText, testId: "sidebar-nav-quotations" },
    ]},
    { key: "booking", label: "Booking & Pelaksanaan", icon: CalendarCheck, items: [
      { path: "/bookings", label: "Daftar Booking", icon: CalendarCheck, testId: "sidebar-nav-bookings" },
      { path: "/bookings/tasks", label: "Tugas per Booking", icon: CheckSquare, testId: "sidebar-nav-bookings-tasks" },
      { path: "/calendar", label: "Kalender & Jadwal", icon: CalendarIcon, testId: "sidebar-nav-calendar" },
      { path: "/overload-planner", label: "Peta Kapasitas", icon: CalendarIcon, testId: "sidebar-nav-overload" },
      { path: "/tasks", label: "Tugas & Progres", icon: CheckSquare, testId: "sidebar-nav-tasks" },
      { path: "/tasks/board", label: "Kanban Board", icon: Grid3X3, testId: "sidebar-nav-tasks-board" },
      { path: "/production", label: "Produksi PBH", icon: Film, testId: "sidebar-nav-production" },
    ]},
  ];

  const financeItems = [];
  if (canFinance) {
    financeItems.push(
      { path: "/finance", label: "Ringkasan Keuangan", icon: Wallet, testId: "sidebar-nav-finance" },
      { path: "/approvals", label: "Persetujuan Masuk", icon: ShieldCheck, testId: "sidebar-nav-approvals" },
      { path: "/invoices", label: "Invoice Client", icon: Receipt, testId: "sidebar-nav-invoices" },
      { path: "/vendors", label: "Tagihan Vendor", icon: Building2, testId: "sidebar-nav-vendors" },
    );
  } else if (isMuaAdmin(user)) {
    financeItems.push(
      { path: "/invoices", label: "Invoice Client", icon: Receipt, testId: "sidebar-nav-invoices" },
      { path: "/vendors", label: "Tagihan Vendor", icon: Building2, testId: "sidebar-nav-vendors" },
    );
  }
  if (financeItems.length) {
    groups.push({ key: "keuangan", label: "Keuangan", icon: Wallet, items: financeItems });
  }

  groups.push({ key: "tim", label: "Tim & Karyawan", icon: UsersRound, items: [
    { path: "/employees", label: "Data Karyawan", icon: UsersRound, testId: "sidebar-nav-employees" },
    ...(canFinance || isMuaAdmin(user) ? [{ path: "/wages", label: "Honor Crew", icon: CreditCard, testId: "sidebar-nav-wages" }] : []),
  ]});

  const produkItems = [];
  if (owner || gm) {
    produkItems.push({ path: "/master-products", label: "Katalog Produk & Portofolio", icon: Package, testId: "sidebar-nav-master-products" });
    produkItems.push({ path: "/landing-cms", label: "Kelola Homepage", icon: Home, testId: "sidebar-nav-landing-cms" });
  }
  if (canFinance) {
    produkItems.push({ path: "/pricelist", label: "Harga & Paket Operasional", icon: FileText, testId: "sidebar-nav-pricelist" });
  }
  if (produkItems.length) {
    groups.push({ key: "produk", label: "Produk & Website", icon: Package, items: produkItems });
  }

  groups.push({ key: "laporan", label: "Marketing & Laporan", icon: BarChart3, items: [
    { path: "/reports", label: "Laporan", icon: BarChart3, testId: "sidebar-nav-reports" },
    { path: "/content", label: "Konten & Kalender Sosial", icon: Video, testId: "sidebar-nav-content" },
  ]});

  const settingsItems = [];
  if (isCentral || dm) settingsItems.push({ path: "/imports", label: "Import Data", icon: Upload, testId: "sidebar-nav-imports" });
  if (owner) settingsItems.push({ path: "/employees/pending", label: "Verifikasi Karyawan", icon: UsersRound, testId: "sidebar-nav-pending" });
  if (owner || gm) settingsItems.push({ path: "/recycle-bin", label: "Recycle Bin", icon: Trash2, testId: "sidebar-nav-recycle-bin" });
  if (settingsItems.length) {
    groups.push({ key: "pengaturan", label: "Pengaturan", icon: Settings, items: settingsItems });
  }
  return groups;
};

const NAV_STATE_KEY = "dwos_sidebar_open_groups_v1";

const NavGroupBlock = ({ group, currentPath, onNav, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const activeInside = group.items.some((it) => currentPath === it.path || currentPath.startsWith(it.path + "/"));
  // Auto-expand kalau ada item aktif di dalam
  useEffect(() => { if (activeInside && !open) setOpen(true); }, [activeInside]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = () => {
    setOpen((v) => {
      const nv = !v;
      try {
        const st = JSON.parse(localStorage.getItem(NAV_STATE_KEY) || "{}");
        st[group.key] = nv; localStorage.setItem(NAV_STATE_KEY, JSON.stringify(st));
      } catch {}
      return nv;
    });
  };
  const Icon = group.icon;
  return (
    <div className="mb-1" data-testid={`sidebar-group-${group.key}`}>
      <button onClick={toggle}
        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-[11px] uppercase tracking-widest font-semibold transition-colors ${activeInside ? "text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}
        data-testid={`sidebar-group-toggle-${group.key}`}>
        <Icon className="w-3.5 h-3.5" />
        <span className="flex-1 text-left">{group.label}</span>
        {open ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRight className="w-3.5 h-3.5"/>}
      </button>
      {open && (
        <div className="mt-1 ml-3 pl-3 border-l border-white/10 space-y-0.5">
          {group.items.map((n) => (
            <NavLink key={n.path} to={n.path} data-testid={n.testId} onClick={onNav}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-white/10 text-white font-medium" : "text-zinc-300 hover:bg-white/5 hover:text-white"
                }`
              }>
              <n.icon className="w-4 h-4" />
              <span className="truncate">{n.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

const SidebarInner = ({ user, groups, onLogout, onNav, currentPath }) => {
  // Baca state open dari localStorage — default: buka group yang berisi halaman aktif + Beranda
  let saved = {};
  try { saved = JSON.parse(localStorage.getItem(NAV_STATE_KEY) || "{}"); } catch {}
  return (
    <div className="h-full flex flex-col text-white sidebar-bg">
      <div className="px-6 py-8 border-b border-zinc-800 flex items-center justify-center">
        <img src="/brand/dwos-logo-light.png" alt="DWOS — Dessy Wedding OS"
          className="w-[110px] h-auto object-contain select-none" draggable={false}/>
      </div>
      <nav className="flex-1 px-3 py-4 overflow-y-auto scrollbar-thin">
        {groups.map((g) => {
          const activeInside = g.items.some((it) => currentPath === it.path || currentPath.startsWith(it.path + "/"));
          const defaultOpen = saved[g.key] !== undefined ? saved[g.key] : (activeInside || g.key === "beranda");
          return <NavGroupBlock key={g.key} group={g} currentPath={currentPath} onNav={onNav} defaultOpen={defaultOpen}/>;
        })}
      </nav>
      <div className="p-4 border-t border-zinc-800">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-brand flex items-center justify-center text-sm font-semibold">
            {(user?.name || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-[11px] text-zinc-400 truncate">
              {ROLE_LABEL[user?.role] || user?.role}
              {isMuaAdmin(user) && <span className="ml-1 text-brand">• Kantor Pusat</span>}
            </div>
          </div>
        </div>
        <Button onClick={onLogout} variant="ghost" size="sm" data-testid="btn-logout"
          className="w-full justify-start text-zinc-300 hover:bg-white/5 hover:text-white">
          <LogOut className="w-4 h-4 mr-2" /> Keluar
        </Button>
        <div className="pt-2 mt-2 border-t border-white/10">
          <RolePreviewButton />
        </div>
      </div>
    </div>
  );
};

const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);
  useEffect(() => {
    if (!q || q.length < 2) { setRes(null); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setRes(data); setOpen(true); }
      catch (e) { console.warn("Global search failed:", e?.message); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const go = (p) => { setOpen(false); setQ(""); nav(p); };
  return (
    <div className="relative w-full max-w-md" ref={ref}>
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C9096]" />
      <Input placeholder="Cari klien, ID pemesanan, venue..." value={q} onChange={(e) => setQ(e.target.value)}
        onFocus={() => q.length >= 2 && setOpen(true)}
        className="pl-9 pr-8 h-9 bg-[#FAF6EE] border-[#E5E0D8]" data-testid="global-search" />
      {q && (<button onClick={() => { setQ(""); setRes(null); setOpen(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8C9096]"><X className="w-4 h-4" /></button>)}
      {open && res && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#E5E0D8] rounded-md shadow-lg overflow-hidden z-50 max-h-[420px] overflow-y-auto">
          {(res.bookings || []).length > 0 && (<div>
            <div className="px-4 py-2 text-[10px] uppercase tracking-wider font-semibold text-[#8C9096] bg-[#FAF6EE]">Pemesanan</div>
            {res.bookings.map((b) => (
              <button key={b.id} onClick={() => go(`/bookings/${b.id}`)} data-testid={`search-booking-${b.booking_code}`}
                className="w-full text-left px-4 py-2.5 hover:bg-[#FAF6EE] flex items-center justify-between gap-3">
                <div><div className="text-sm font-medium">{b.client_name || b.event_name}</div>
                  <div className="text-[11px] text-[#8C9096]">{b.event_name} • {b.venue || "-"} • {formatDate(b.event_date)}</div></div>
                <span className="font-mono text-[10px] text-brand">{b.booking_code}</span>
              </button>
            ))}
          </div>)}
          {(res.clients || []).length > 0 && (<div>
            <div className="px-4 py-2 text-[10px] uppercase tracking-wider font-semibold text-[#8C9096] bg-[#FAF6EE]">Klien</div>
            {res.clients.map((c) => (
              <button key={c.id} onClick={() => go(`/clients`)} className="w-full text-left px-4 py-2.5 hover:bg-[#FAF6EE]">
                <div className="text-sm font-medium">{clientName(c)}</div>
                <div className="text-[11px] text-[#8C9096]">{c.whatsapp} • {c.address}</div>
              </button>
            ))}
          </div>)}
          {(!res.bookings?.length && !res.clients?.length) && (<div className="p-6 text-center text-sm text-[#8C9096]">Tidak ada hasil</div>)}
        </div>
      )}
    </div>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const groups = buildNavGroups(user);
  const handleLogout = async () => { await logout(); navigate("/login"); };
  return (
    <div className="min-h-screen app-bg">
      <RolePreviewBanner />
      <aside className="hidden lg:block fixed left-0 top-0 h-screen w-64 z-40">
        <SidebarInner user={user} groups={groups} onLogout={handleLogout} currentPath={location.pathname} />
      </aside>
      <div className="lg:pl-64">
        <header className="h-16 px-4 sm:px-6 bg-white/90 backdrop-blur-md border-b border-[#E5E0D8] flex items-center justify-between gap-3 sticky top-0 z-30">
          <div className="flex items-center gap-3 min-w-0">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden shrink-0" data-testid="btn-mobile-menu"><Menu className="w-5 h-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64 sidebar-bg border-none">
                <SidebarInner user={user} groups={groups} onLogout={handleLogout} onNav={() => setOpen(false)} currentPath={location.pathname} />
              </SheetContent>
            </Sheet>
            <div className="hidden sm:block min-w-0">
              <div className="font-display text-base font-semibold text-[#1E2022] truncate">Selamat datang, {user?.name?.split(" ")[0]}</div>
              <div className="text-xs text-[#8C9096]">
                {ROLE_LABEL[user?.role]}
                {isMuaAdmin(user) && <span className="ml-1 text-brand">• Admin Kantor Pusat</span>}
              </div>
            </div>
          </div>
          <div className="flex-1 max-w-md"><GlobalSearch /></div>
          <NotificationBell />
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
